<template>
  <v-layout v-if="!isMobile" justify-end align-center>
    <full-screen-spinner
      v-if="showSpinner"
      spinner-text="Logging out..."
      top-margin="0px"
    />
    <v-flex class="toolbar__search" :style="toolbarStyle">
      <auto-suggestion-input v-if="displayClientSearch" :header-mode="true" />
    </v-flex>
    <v-flex shrink>
      <v-btn flat icon @click.stop="triggerInviteBuyerModal">
        <v-icon :color="colors.hwayzDarkSlate">
          people
        </v-icon>
      </v-btn>
      <v-btn flat icon color="grey" @click="openCommunicationTab">
        <span
          v-if="agentUnreadMessages && agentUnreadMessages.length"
          class="header-client__unread-messages-count"
        >{{ agentUnreadMessages.length }}</span>
        <v-icon :color="colors.pending">
          email
        </v-icon>
      </v-btn>
    </v-flex>
    <v-flex v-ripple shrink>
      <menu-account
        :user="agentData"
        :items="menuItems"
        @sign-out="signOut"
        @open-account="$router.push({ name: 'agent-account' })"
      />
    </v-flex>
    <v-flex v-if="getWindowWidth < 1264" class="pa-0" shrink>
      <v-toolbar-side-icon
        class="navigation-toggle"
        @click.stop="toggleDrawer"
      />
    </v-flex>
    <v-flex v-if="isMobile" x12 md7 lg5 class="text-center">
      <label v-if="isViewDetails" class="toolbar__title">VIEW DETAILS</label>
      <label v-else class="toolbar__title">{{ getCurrentPageName }}</label>
    </v-flex>
    <v-flex shrink class="toolbar__icons">
      <div class="toolbar__toggle">
        <v-toolbar-side-icon
          v-if="isMobile"
          class="navigation-toggle"
          @click.stop="toggleDrawer"
        />
      </div>
    </v-flex>
  </v-layout>
  <!-- Mobile layout -->
  <v-layout v-else id="toolbarMobile" align-center justify-start>
    <v-flex v-if="showMobileBackButton" shrink @click="handlerMobileBackButton">
      <v-icon v-if="isMobile" class="white--text">
        chevron_left
      </v-icon>
    </v-flex>
    <v-flex x12 md7 lg5 class="text-center">
      <label v-if="isViewDetails" class="toolbar__title">VIEW DETAILS</label>
      <label v-else class="toolbar__title">{{ getCurrentPageName }}</label>
    </v-flex>
    <v-flex shrink class="text-xs-right">
      <v-toolbar-side-icon
        class="navigation-toggle hwayzHeaderClient"
        @click.stop="toggleDrawer"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import { routeNamesForClientSearch } from '../../utils/generalConsts'

import AutoSuggestionInput from '../uiUtils/AutoSuggestionInput.vue'
import FullScreenSpinner from '../uiUtils/FullScreenSpinner.vue'
import MenuAccount from '../common/navigation/MenuAccount.vue'

import colors from '../../scss/_colors.scss'

export default {
  components: {
    AutoSuggestionInput,
    MenuAccount,
    FullScreenSpinner
  },

  props: {
    isMiniSidebar: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      colors: colors,
      displayClientSearch: false,
      showSpinner: false,
      menuItems: [
        {
          text: 'Account',
          icon: 'account_circle',
          callback: 'open-account'
        },
        {
          text: 'Log Out',
          icon: 'exit_to_app',
          callback: 'sign-out'
        }
      ]
    }
  },

  computed: {
    ...mapState('agent', ['selectedDialogUser', 'agentData']),
    ...mapState('mobile', ['showMobileBackButton']),
    ...mapState('communication', ['selectedThread', 'showNewThreadForm']),
    ...mapState('home', ['activeRoute']),
    ...mapState('common', [
      'lastNewQuery',
      'isMobile',
      'drawer',
      'showMobileFeedbackModal',
      'showSingleRoutePropertyView'
    ]),
    ...mapGetters('agent', ['agentUnreadMessages']),

    getWindowWidth() {
      const windowWidth = this.$store.state.home.windowWidth
      return windowWidth
    },

    toolbarStyle() {
      if (this.getWindowWidth < 1080) {
        return { width: 'auto', left: 0 }
      }
      let width = 48
      if (this.getWindowWidth >= 1264) {
        width = this.isMiniSidebar ? 128 : 258
      }
      return {
        width: `calc(100% - ${width}px)`,
        left: `calc(${width}px - 2rem)`
      }
    },

    getCurrentPageName() {
      if (this.$store.state.home.activeRoute) {
        return this.$store.state.home.activeRoute.meta.title.toUpperCase()
      }
      return ''
    },

    isViewDetails() {
      const currentPath = this.$route.path
      const splitted = currentPath.split('/')

      if (splitted.length >= 4 && splitted[3] && splitted[2] === 'search') {
        return true
      } else {
        return false
      }
    }
  },

  watch: {
    $route(to, from) {
      const currentRouteName = this.$router.currentRoute.name
      this.displayClientSearch =
        currentRouteName &&
        routeNamesForClientSearch.includes(currentRouteName)
    }
  },

  mounted() {
    const currentRouteName = this.$router.currentRoute.name
    this.displayClientSearch =
      currentRouteName && routeNamesForClientSearch.includes(currentRouteName)
  },

  methods: {
    ...mapMutations('common', {
      setDrawerState: 'SET_DRAWER_STATE',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL',
      setShowSingleRoutePropertyView: 'SET_SHOW_SINGLE_ROUTE_PROPERTY_VIEW',
      resetMultipleRequestedObject: 'RESET_MULTIPLE_REQUESTED_OBJECT',
      resetSearchTextParams: 'RESET_SEARCH_TEXT_PARAMS',
      clearLastNewQuery: 'CLEAR_LAST_NEW_QUERY',
      setAddressInputValue: 'SET_ADDRESS_INPUT_VALUE'
    }),
    ...mapMutations('mobile', {
      setAllRoutesShowing: 'SET_ALL_ROUTES_SHOWING',
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    ...mapMutations('agent', {
      setDialogUser: 'SET_DIALOG_USER'
    }),
    ...mapMutations('communication', {
      setThread: 'SET_THREAD',
      setShowNewThreadForm: 'SET_SHOW_NEW_THREAD_FORM'
    }),
    ...mapMutations('client', {
      setClientData: 'SET_CLIENT_DATA',
      setAssignToClient: 'SET_ASSIGN_TO_CLIENT'
    }),
    ...mapActions('common', ['profileLogout']),
    toggleDrawer() {
      this.setDrawerState(!this.drawer)
    },

    openCommunicationTab() {
      this.$router.push({ name: 'communication' })
    },

    triggerInviteBuyerModal() {
      this.$emit('show-invite-buyer-modal')
    },

    handlerMobileBackButton() {
      if (this.activeRoute.name === 'feedback') {
        return this.resolveMobileFeedbackHandler()
      }
      if (this.activeRoute.name === 'calendar') {
        return this.resolveMobileCalendarHandler()
      }
      if (this.activeRoute.name === 'communication') {
        if (this.showSingleRoutePropertyView) {
          return this.setShowSingleRoutePropertyView(false)
        }
        if (this.showNewThreadForm) {
          return this.setShowNewThreadForm(false)
        }
        if (this.selectedThread) {
          return this.setThread(null)
        }

        this.setShowingMobileBackButton(false)
        return this.setDialogUser()
      }
      if (this.activeRoute.name === 'agent-search-results') {
        this.resetMultipleRequestedObject()
        this.resetSearchTextParams()
        this.clearLastNewQuery()
        this.setAssignToClient({})
        this.setAddressInputValue({ text: '', suggestion: false })
        this.$router.go(-1)
      }
    },

    resolveMobileFeedbackHandler() {
      if (this.showMobileFeedbackModal) {
        this.setShowMobileFeedbackModal(false)
        return
      }
      this.setClientData(null)
      this.setShowingMobileBackButton(false)
    },

    resolveMobileCalendarHandler() {
      if (this.showSingleRoutePropertyView) {
        return this.setShowSingleRoutePropertyView(false)
      }
      if (this.showMobileFeedbackModal) {
        return this.setShowMobileFeedbackModal(false)
      }
      this.setShowingMobileBackButton(false)
      return this.setAllRoutesShowing(true)
    },
    signOut() {
      this.showSpinner = true
      setTimeout(() => {
        this.showSpinner = false
        this.profileLogout()
      }, 1500)
    }
  }
}
</script>

<style lang="sass">
.header-client__unread-messages-count
  position: absolute
  background: $hwayzNavigationTab
  color: white
  border-radius: 50%
  width: 15px
  font-size: 10px
  display: block
  text-align: center
  height: 15px
  padding-top: 0px
  top: -4px
  right: 2px

.toolbar__title
  font-size: $hwayzFont22rem
  color: $hwayzBlack

.toolbar__search
  position: absolute
  padding: 0 2rem
  top: 0
  height: 48px
  line-height: 52px
  @media only screen and (max-width: 1079px)
    position: relative
  @media only screen and (max-width: 760px)
    padding-left: 0
    padding-right: 0.5rem

.toolbar__button-group
  .v-btn
    @media only screen and (max-width: 760px)
      font-size: $hwayzFont1rem1 !important
      padding: 0px 10px

.toolbar__icons
  display: flex
  justify-content: space-between
  align-items: center
  @media only screen and (min-width: 760px)
    justify-content: flex-end
    .v-icon
      margin: 0 1rem

#headerAgent, #headerClient
  z-index: 10 !important
  &.mobile-toolbar
    background: $hwayzBrandGold
    .toolbar__title
      color: $hwayzWhite
    .navigation-toggle
      .v-btn__content i
        color: $hwayzWhite

#toolbarMobile i
    font-size: 2.8rem

.navigation-toggle
  .v-btn__content
    i
      color: $hwayzBlack
</style>
